import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaArrowRight, FaFilter, FaGraduationCap, FaPen, FaPlus, FaSearch, FaTimes, FaTrash } from "react-icons/fa";
import { AuthContext } from "../../../AuthProvider";
import ErrorPage from "../../../components/ErrorPage";
import Modal from "../../../components/Modal";
import SchoolYearDropdown, { getSchoolYearBucket } from "../../../components/SchoolYearDropdown";
import Spinner from "../../../components/Spinner";
import { useCan } from "../../../authz/useCan";
import { translateClassName } from "../../../utils/classroomUtils";
import { deleteClassroom } from "../api";
import { getCachedClassrooms, hasClassroomListCache, loadClassroomList, removeCachedClassroom } from "../cache";
import { ActionButton, AvatarBlock, EmptyState, IconActionButton, LdsPage, ListSection, SearchPanel, StatusBadge, listRowClass } from "../components/Lds";
import type { AppError, Classroom, EntityId } from "../types";
import { compareClassroomNames, compareClassroomTypes } from "./classroomSorting";
import {
  classroomChildren,
  classroomYearLabel,
  computeGenderStats,
  computeKindergartenAgeDistribution,
  entityKey,
  getYearNumber,
  httpStatus,
  teacherName,
} from "../utils";

type SortColumn = "className" | "startDate" | null;
type SortDirection = "asc" | "desc";
type DistributionKey = "young" | "middle" | "old" | "male" | "female" | "other";

interface DistributionSegment {
  key: DistributionKey;
  count: number;
  label: string;
  colorClassName: string;
}

const DistributionBar = ({ label, total, segments }: { label: string; total: number; segments: DistributionSegment[] }) => (
  <div className="min-w-[180px] max-w-xs">
    <div className="mb-1 flex items-center justify-between gap-3 text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">
      <span>{label}</span>
      <span>{total}</span>
    </div>
    <div className="flex h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
      {segments.map((segment) => {
        if (!segment.count) return null;
        const percent = (segment.count / total) * 100;
        return (
          <div
            key={segment.key}
            className={`${segment.colorClassName} h-full`}
            style={{ width: `${percent}%`, minWidth: "4px" }}
            title={`${segment.label}: ${segment.count}`}
          />
        );
      })}
    </div>
  </div>
);

const Classrooms = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { keycloak } = useContext(AuthContext);

  const canViewGrant = useCan("school.classrooms", "view");
  const canReadGrant = useCan("school.classrooms", "read");
  const canListGrant = useCan("school.classrooms", "list");
  const canCrudGrant = useCan("school.classrooms", "crud");
  const canManageGrant = useCan("school.classrooms", "manage");
  const canView = canViewGrant || canReadGrant || canListGrant;
  const canCrud = canCrudGrant || canManageGrant;
  const canLinkSelf = useCan("school.teachers", "linkSelf");
  const currentSub = keycloak?.tokenParsed?.sub ? String(keycloak.tokenParsed.sub) : null;
  const isTeacherMode = Boolean(canLinkSelf && !canCrud);

  const [classrooms, setClassrooms] = useState<Classroom[]>(() => getCachedClassrooms() ?? []);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [selectedYearKey, setSelectedYearKey] = useState("ALL");
  const [defaultYearKey, setDefaultYearKey] = useState("ALL");
  const [showMineOnly, setShowMineOnly] = useState(isTeacherMode);
  const [loading, setLoading] = useState(() => !hasClassroomListCache());
  const [error, setError] = useState<AppError | null>(null);
  const [classroomToDelete, setClassroomToDelete] = useState<EntityId | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterPopoverRef = useRef<HTMLDivElement | null>(null);
  const defaultYearAppliedRef = useRef(false);

  useEffect(() => {
    setShowMineOnly(isTeacherMode);
  }, [isTeacherMode]);

  useEffect(() => {
    if (!filterOpen || typeof document === "undefined") return undefined;

    const closeOnOutsideInteraction = (event: MouseEvent | FocusEvent) => {
      const target = event.target;
      if (target instanceof Node && filterPopoverRef.current?.contains(target)) return;
      setFilterOpen(false);
    };

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFilterOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideInteraction);
    document.addEventListener("focusin", closeOnOutsideInteraction);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideInteraction);
      document.removeEventListener("focusin", closeOnOutsideInteraction);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [filterOpen]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!canView && !canCrud) {
        setError({ message: t("error.403.message", { defaultValue: "You do not have permission." }), status: 403 });
        setLoading(false);
        return;
      }
      try {
        const data = await loadClassroomList();
        if (!cancelled) setClassrooms(data);
      } catch (err) {
        if (!cancelled && !hasClassroomListCache()) {
          setError({
            message:
              httpStatus(err) === 403
                ? t("error.403.message", { defaultValue: "You do not have permission." })
                : t("classrooms.overview.load_failed", { defaultValue: "Failed to load classrooms." }),
            status: httpStatus(err),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [canCrud, canView, t]);

  const duplicateGradeIds = useMemo(() => {
    const groups = new Map<string, EntityId[]>();
    classrooms.forEach((classroom) => {
      if (classroom.classType !== "GRADE" || classroom.grade == null) return;
      const key = `${classroom.schoolYear?.id ?? "unknown"}-${classroom.grade}`;
      groups.set(key, [...(groups.get(key) ?? []), classroom.id]);
    });
    return new Set(Array.from(groups.values()).filter((ids) => ids.length > 1).flat().map(String));
  }, [classrooms]);

  const yearOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string; label: string; startDate?: string; endDate?: string }>();
    classrooms.forEach((classroom) => {
      const startY = getYearNumber(classroom.startDate);
      const endY = getYearNumber(classroom.endDate);
      const id = classroom.schoolYear?.id != null ? String(classroom.schoolYear.id) : startY && endY ? `RANGE:${startY}-${endY}` : null;
      if (!id || map.has(id)) return;
      const label = classroomYearLabel(classroom) || t("classrooms.filters.unknown_year", { defaultValue: "Unknown year" });
      map.set(id, { id, name: label, label, startDate: classroom.schoolYear?.startDate as string | undefined, endDate: classroom.schoolYear?.endDate as string | undefined });
    });
    return [
      { id: "ALL", name: t("classrooms.filters.all_years", { defaultValue: "All school years" }), label: t("classrooms.filters.all_years", { defaultValue: "All school years" }) },
      ...Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [classrooms, t]);

  useEffect(() => {
    if (defaultYearAppliedRef.current) return;
    const currentYear = yearOptions.find(
      (year) => year.id !== "ALL" && getSchoolYearBucket(year) === "active",
    );
    if (!currentYear) return;
    const currentYearKey = currentYear.id;
    setDefaultYearKey(currentYearKey);
    setSelectedYearKey((selected) => selected === "ALL" ? currentYearKey : selected);
    defaultYearAppliedRef.current = true;
  }, [yearOptions]);

  const isMyClassroom = useCallback((classroom: Classroom) => {
    if (!currentSub) return false;
    return (classroom.teachers ?? []).some(
      (teacher) => !teacher.endDate && [teacher.keycloakUserId, teacher.userId, teacher.keycloakId].some((value) => value === currentSub)
    );
  }, [currentSub]);

  const sortedClassrooms = useMemo(() => {
    let list = [...classrooms];
    if (showMineOnly && currentSub) list = list.filter(isMyClassroom);
    if (selectedYearKey !== "ALL") {
      list = list.filter((classroom) => {
        if (selectedYearKey.startsWith("RANGE:")) {
          const [, range] = selectedYearKey.split(":");
          const [start, end] = range.split("-").map(Number);
          return getYearNumber(classroom.startDate) === start && getYearNumber(classroom.endDate) === end;
        }
        return String(classroom.schoolYear?.id ?? "") === selectedYearKey;
      });
    }
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      list = list.filter((classroom) => `${classroom.className ?? ""} ${classroomYearLabel(classroom)}`.toLowerCase().includes(query));
    }
    const direction = sortDirection === "asc" ? 1 : -1;
    return list.sort((a, b) => {
      const typeDiff = compareClassroomTypes(a, b);
      const nameDiff = compareClassroomNames(translateClassName(a), translateClassName(b));
      if (sortColumn === "className") return typeDiff || direction * nameDiff;
      if (sortColumn === "startDate") return direction * ((getYearNumber(a.startDate) ?? 0) - (getYearNumber(b.startDate) ?? 0));
      const yearDiff = (getYearNumber(a.startDate) ?? 0) - (getYearNumber(b.startDate) ?? 0);
      return yearDiff || typeDiff || nameDiff;
    });
  }, [classrooms, currentSub, isMyClassroom, searchQuery, selectedYearKey, showMineOnly, sortColumn, sortDirection]);

  const sortValue = `${sortColumn ?? "default"}:${sortDirection}`;
  const defaultShowMineOnly = isTeacherMode;
  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    selectedYearKey !== defaultYearKey ||
    showMineOnly !== defaultShowMineOnly ||
    sortColumn !== null ||
    sortDirection !== "asc";

  const handleSortChange = (value: string) => {
    switch (value) {
      case "className:asc":
        setSortColumn("className");
        setSortDirection("asc");
        break;
      case "className:desc":
        setSortColumn("className");
        setSortDirection("desc");
        break;
      case "startDate:asc":
        setSortColumn("startDate");
        setSortDirection("asc");
        break;
      case "startDate:desc":
        setSortColumn("startDate");
        setSortDirection("desc");
        break;
      default:
        setSortColumn(null);
        setSortDirection("asc");
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedYearKey(defaultYearKey);
    setShowMineOnly(defaultShowMineOnly);
    setSortColumn(null);
    setSortDirection("asc");
  };

  const activePanelFilterCount =
    Number(showMineOnly !== defaultShowMineOnly) +
    Number(sortColumn !== null || sortDirection !== "asc");

  const clearPanelFilters = () => {
    setShowMineOnly(defaultShowMineOnly);
    setSortColumn(null);
    setSortDirection("asc");
  };

  const handleDelete = async () => {
    if (!classroomToDelete) return;
    try {
      await deleteClassroom(classroomToDelete);
      removeCachedClassroom(classroomToDelete);
      setClassrooms((prev) => prev.filter((classroom) => String(classroom.id) !== String(classroomToDelete)));
      setClassroomToDelete(null);
    } catch (err) {
      setError({
        message:
          httpStatus(err) === 403
            ? t("error.403.message", { defaultValue: "You do not have permission." })
            : t("classrooms.management.delete_failed", { defaultValue: "Failed to delete classroom." }),
        status: httpStatus(err),
      });
    }
  };

  if (loading && classrooms.length === 0) return <Spinner />;
  if (error) return <ErrorPage message={error.message} status={error.status} onRetry={() => window.location.reload()} />;

  return (
    <LdsPage>
      <div className="space-y-5">
        <section className="relative overflow-hidden rounded-[28px] border border-sky-100 bg-gradient-to-r from-sky-50 via-white to-cyan-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-sky-950/35 dark:via-slate-950 dark:to-cyan-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 flex-col gap-5 lg:flex-row lg:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-sky-200 to-cyan-50 text-3xl text-sky-700 shadow-lg dark:border-slate-900 dark:from-sky-800 dark:to-cyan-950 dark:text-sky-100 sm:h-24 sm:w-24">
                <FaGraduationCap aria-hidden="true" />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("school.overview.title", { defaultValue: "School structure" })}
                </p>
                <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                  {t("classrooms.overview.title", { defaultValue: "Classrooms" })}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                  {t("classrooms.overview.description", { defaultValue: "View and manage classrooms, teachers and school year placement." })}
                </p>
                {(loading && classrooms.length > 0) || duplicateGradeIds.size > 0 ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {loading && classrooms.length > 0 ? <StatusBadge tone="info">{t("common.refreshing", { defaultValue: "Refreshing" })}</StatusBadge> : null}
                    {duplicateGradeIds.size > 0 ? <StatusBadge tone="warn">{t("classrooms.overview.duplicate_grades_warning", { defaultValue: "Duplicate grades in the same school year" })}</StatusBadge> : null}
                  </div>
                ) : null}
              </div>
            </div>

            {canCrud ? (
              <div className="ml-auto shrink-0 lg:mr-36">
                <ActionButton onClick={() => navigate("/school/classrooms/new")}>
                  <span className="inline-flex items-center gap-2">
                    <FaPlus aria-hidden="true" />
                    {t("classrooms.overview.add_button", { defaultValue: "Add classroom" })}
                  </span>
                </ActionButton>
              </div>
            ) : null}
          </div>

          <div className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex" aria-hidden="true">
            <FaGraduationCap className="text-[110px] text-sky-200/70 dark:text-sky-800/40" />
          </div>
        </section>

        <section className="sticky top-4 z-40 rounded-[26px] border border-slate-200/80 bg-white/95 p-4 shadow-[0_18px_50px_rgba(15,23,42,0.10)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/90 sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <label className="relative flex-1">
              <span className="sr-only">
                {t("classrooms.overview.search", { defaultValue: "Search classrooms" })}
              </span>
              <FaSearch
                aria-hidden="true"
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t("classrooms.overview.search", { defaultValue: "Search classrooms..." })}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50/70 pl-11 pr-4 text-sm text-slate-950 outline-none transition focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-100 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-indigo-400 dark:focus:bg-white/10 dark:focus:ring-indigo-400/10"
              />
            </label>

            <div className="w-full shrink-0 lg:w-72">
              <SchoolYearDropdown
                schoolYears={yearOptions}
                selectedSchoolYearId={selectedYearKey}
                onChange={setSelectedYearKey}
                label={t("classrooms.filters.school_year", { defaultValue: "School year" })}
              />
            </div>

            <div ref={filterPopoverRef} className="relative">
              <button
                type="button"
                onClick={() => setFilterOpen((open) => !open)}
                className={`inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl border text-sm font-bold transition focus:outline-none focus:ring-2 focus:ring-indigo-500 lg:w-auto ${
                  filterOpen || activePanelFilterCount > 0
                    ? "border-indigo-300 bg-indigo-600 text-white shadow-lg shadow-indigo-900/15 hover:bg-indigo-700 dark:border-indigo-400 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                    : "border-slate-200 bg-white text-slate-700 hover:border-indigo-200 hover:text-indigo-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                } ${activePanelFilterCount > 0 ? "pr-10" : ""}`}
                aria-expanded={filterOpen}
                aria-haspopup="dialog"
              >
                <span className="inline-flex h-full items-center justify-center gap-2 px-5">
                  <FaFilter aria-hidden="true" />
                  {t("common.filter", { defaultValue: "Filter" })}
                </span>
                {activePanelFilterCount > 0 ? (
                  <span className="mr-1 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-white/20 px-1.5 text-xs text-white">
                    {activePanelFilterCount}
                  </span>
                ) : null}
              </button>

              {activePanelFilterCount > 0 ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    clearPanelFilters();
                    setFilterOpen(false);
                  }}
                  className="absolute right-1 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl text-white transition hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/70"
                  aria-label={t("common.clear", { defaultValue: "Clear" })}
                >
                  <FaTimes aria-hidden="true" />
                </button>
              ) : null}

              {filterOpen ? (
                <div
                  role="dialog"
                  aria-label={t("common.filter", { defaultValue: "Filter" })}
                  className="absolute right-0 z-[70] mt-3 w-[min(360px,calc(100vw-2rem))] rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_24px_60px_rgba(15,23,42,0.18)] dark:border-white/10 dark:bg-slate-900"
                >
                  {(isTeacherMode || canLinkSelf) ? (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={showMineOnly}
                      onClick={() => setShowMineOnly((current) => !current)}
                      className="flex w-full items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-left dark:border-white/10 dark:bg-white/5"
                    >
                      <span>
                        <span className="block text-sm font-bold text-slate-800 dark:text-slate-100">
                          {t("classrooms.filters.only_mine", { defaultValue: "My classrooms" })}
                        </span>
                        <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                          {t("classrooms.filters.only_mine_description", {
                            defaultValue: "Only show classrooms assigned to me.",
                          })}
                        </span>
                      </span>
                      <span
                        aria-hidden="true"
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
                          showMineOnly ? "bg-indigo-500" : "bg-slate-300 dark:bg-slate-700"
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                            showMineOnly ? "translate-x-5" : "translate-x-1"
                          }`}
                        />
                      </span>
                    </button>
                  ) : null}

                  <label className={`${isTeacherMode || canLinkSelf ? "mt-4" : ""} block`}>
                    <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {t("classrooms.filters.sort_by", { defaultValue: "Sort by" })}
                    </span>
                    <select
                      aria-label={t("classrooms.filters.sort_by", { defaultValue: "Sort by" })}
                      value={sortValue}
                      onChange={(event) => handleSortChange(event.target.value)}
                      className="h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20 dark:border-white/10 dark:bg-slate-950/50 dark:text-slate-200"
                    >
                      <option value="default:asc">
                        {t("classrooms.filters.default_sort", { defaultValue: "Default order" })}
                      </option>
                      <option value="className:asc">
                        {t("classrooms.filters.name_asc", { defaultValue: "Name: A–Z" })}
                      </option>
                      <option value="className:desc">
                        {t("classrooms.filters.name_desc", { defaultValue: "Name: Z–A" })}
                      </option>
                      <option value="startDate:asc">
                        {t("classrooms.filters.year_asc", { defaultValue: "School year: ascending" })}
                      </option>
                      <option value="startDate:desc">
                        {t("classrooms.filters.year_desc", { defaultValue: "School year: descending" })}
                      </option>
                    </select>
                  </label>

                  <div className="mt-4 flex justify-between gap-2">
                    <button
                      type="button"
                      onClick={clearPanelFilters}
                      className="rounded-full px-4 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
                    >
                      {t("common.clear", { defaultValue: "Clear" })}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFilterOpen(false)}
                      className="rounded-full bg-indigo-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                    >
                      {t("common.close", { defaultValue: "Close" })}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <ListSection
          title={t("classrooms.overview.list_title", { defaultValue: "Classroom overview" })}
          count={t("common.result_count", { count: sortedClassrooms.length, defaultValue: "{{count}} results" })}
        >
          {sortedClassrooms.map((classroom) => {
            const activeChildren = classroomChildren(classroom);
            const ageStats = computeKindergartenAgeDistribution(classroom, activeChildren);
            const genderStats = computeGenderStats(activeChildren);
            const activeTeachers = (classroom.teachers ?? []).filter((teacher) => !teacher.endDate);
            const name = translateClassName(classroom);
            return (
              <article
                key={entityKey(classroom.id)}
                onClick={() => navigate(`/school/classrooms/${classroom.id}/details`)}
                className={`${listRowClass} lg:grid-cols-[minmax(220px,0.9fr)_minmax(240px,1fr)_minmax(180px,0.8fr)_minmax(180px,0.9fr)_auto]`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <AvatarBlock tone="sky">{name.slice(0, 2)}</AvatarBlock>
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-semibold text-slate-950 dark:text-white">{name}</h2>
                    <div className="mt-1 flex flex-wrap gap-2">
                      <StatusBadge tone="neutral">{classroom.classType === "KINDERGARTEN" ? t("classrooms.kindergarten.label", { defaultValue: "Kindergarten" }) : `${t("classrooms.grade.label", { defaultValue: "Grade" })} ${classroom.grade ?? "—"}`}</StatusBadge>
                      {duplicateGradeIds.has(String(classroom.id)) ? <StatusBadge tone="warn">{t("classrooms.overview.duplicate", { defaultValue: "Duplicate" })}</StatusBadge> : null}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 text-sm text-slate-600 dark:text-slate-300">
                  <StatusBadge tone="neutral">{activeChildren.length} {t("classrooms.details.children", { defaultValue: "children" })}</StatusBadge>
                  {ageStats ? (
                    <DistributionBar
                      label={t("classrooms.kindergarten_age_distribution", { defaultValue: "Age distribution" })}
                      total={ageStats.total}
                      segments={[
                        { key: "young", count: ageStats.counts.young, label: t("classrooms.kindergarten_ages.young", { defaultValue: "Youngest kindergarten children" }), colorClassName: "bg-emerald-500" },
                        { key: "middle", count: ageStats.counts.middle, label: t("classrooms.kindergarten_ages.middle", { defaultValue: "Middle kindergarten children" }), colorClassName: "bg-amber-500" },
                        { key: "old", count: ageStats.counts.old, label: t("classrooms.kindergarten_ages.old", { defaultValue: "Oldest kindergarten children" }), colorClassName: "bg-purple-600" },
                      ]}
                    />
                  ) : null}
                  {genderStats ? (
                    <DistributionBar
                      label={t("classrooms.kindergarten_gender_distribution", { defaultValue: "Gender distribution" })}
                      total={genderStats.total}
                      segments={[
                        { key: "male", count: genderStats.counts.male, label: t("classrooms.gender.male", { defaultValue: "Boys" }), colorClassName: "bg-blue-500" },
                        { key: "female", count: genderStats.counts.female, label: t("classrooms.gender.female", { defaultValue: "Girls" }), colorClassName: "bg-pink-400" },
                        { key: "other", count: genderStats.counts.other, label: t("classrooms.gender.other", { defaultValue: "Other" }), colorClassName: "bg-slate-500" },
                      ]}
                    />
                  ) : null}
                </div>

                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{classroomYearLabel(classroom) || "—"}</p>
                <p className="min-w-0 truncate text-sm text-slate-600 dark:text-slate-300">
                  {activeTeachers.length > 0 ? activeTeachers.map((teacher) => teacherName(teacher) || t("classrooms.table.unknown_teacher", { defaultValue: "Unknown teacher" })).join(", ") : t("classrooms.table.no_teacher_assigned", { defaultValue: "No teacher assigned" })}
                </p>
                <div className="flex flex-wrap items-center justify-start gap-2 lg:justify-end" onClick={(event) => event.stopPropagation()}>
                  <ActionButton
                    tone="secondary"
                    onClick={() => {
                      navigate(`/school/classrooms/${classroom.id}/details`);
                    }}
                  >
                    <span className="inline-flex items-center gap-2">{t("common.actions.view", { defaultValue: "View" })}<FaArrowRight aria-hidden="true" /></span>
                  </ActionButton>
                  {canCrud ? (
                    <>
                      <IconActionButton
                        label={t("common.actions.edit", { defaultValue: "Edit" })}
                        onClick={() => {
                          navigate(`/school/classrooms/${classroom.id}/edit`);
                        }}
                      >
                        <FaPen aria-hidden="true" />
                      </IconActionButton>
                      <IconActionButton
                        label={t("common.actions.delete", { defaultValue: "Delete" })}
                        tone="danger"
                        onClick={() => {
                          setClassroomToDelete(classroom.id);
                        }}
                      >
                        <FaTrash aria-hidden="true" />
                      </IconActionButton>
                    </>
                  ) : null}
                </div>
              </article>
            );
          })}
          {sortedClassrooms.length === 0 ? <div className="p-5"><EmptyState>{t("classrooms.common.none_available", { defaultValue: "No classrooms available." })}</EmptyState></div> : null}
        </ListSection>
      </div>

      <Modal
        show={classroomToDelete != null}
        title={t("common.modal.are_you_sure", { defaultValue: "Are you sure?" })}
        message={t("common.modal.action_cannot_be_undone", { defaultValue: "This action cannot be undone." })}
        isConfirm
        type="confirm"
        onConfirm={handleDelete}
        onClose={() => setClassroomToDelete(null)}
      />
    </LdsPage>
  );
};

export default Classrooms;
